import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import React from 'react'
import styled from 'styled-components'

const BoldText = styled(Text)`
  font-weight: 500;
`

export const formatDate = (date: Date | string): string => {
  return dayjs(date).format('HH:mm DD-MM-YYYY')
}

export const formatDateOnly = (date: Date | string): string => {
  return dayjs(date).format('DD-MM-YYYY')
}

export const capitalizeFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

export const getFieldLabel = (
  fieldName: string,
  t: (key: string) => string,
  isDisposal?: boolean,
  isRestock?: boolean
): string => {
  if (fieldName === 'actionTime' && isDisposal) {
    return 'Actioned Date'
  }
  if (fieldName === 'actionTime' && isRestock) {
    return 'Received date'
  }
  if (fieldName === 'discrepancy') {
    return `Discrepancy ${getDiscrepancyFieldLabel('status', t)}`
  }
  if (fieldName.startsWith('discrepancy.')) {
    const subField = fieldName.replace('discrepancy.', '')
    const subFieldLabel = getDiscrepancyFieldLabel(subField, t)
    return `Discrepancy ${subFieldLabel}`
  }
  const fieldKey =
    fieldName === 'notes' ? 'DESCRIPTION' : fieldName.toUpperCase()
  const translation = t(fieldKey as any)
  return translation !== fieldKey ? translation : capitalizeFieldName(fieldName)
}

export const isAttachmentField = (fieldName: string): boolean => {
  const lowerField = fieldName.toLowerCase()
  return lowerField.includes('attachment') || lowerField.includes('media')
}

export const extractAttachmentNames = (attachments: any[]): string[] => {
  return attachments.map((attachment) => {
    if (typeof attachment === 'string') {
      if (attachment.includes('/')) {
        return attachment.split('/').pop() || attachment
      }
      return attachment
    }
    if (attachment && typeof attachment === 'object') {
      return (
        attachment.name ||
        attachment.key?.split('/').pop() ||
        attachment.url?.split('/').pop() ||
        'Unknown'
      )
    }
    return 'Unknown'
  })
}

export const formatAttachmentChange = (
  oldValue: any,
  newValue: any
): string => {
  const oldAttachments = Array.isArray(oldValue) ? oldValue : []
  const newAttachments = Array.isArray(newValue) ? newValue : []

  const oldNames = extractAttachmentNames(oldAttachments)
  const newNames = extractAttachmentNames(newAttachments)

  const added = newNames.filter((name) => !oldNames.includes(name))
  const removed = oldNames.filter((name) => !newNames.includes(name))

  const changes: string[] = []

  if (added.length > 0) {
    const count = added.length
    const names = added.join(', ')
    changes.push(
      `Added ${count} new attachment${count > 1 ? 's' : ''}: ${names}`
    )
  }

  if (removed.length > 0) {
    const count = removed.length
    const names = removed.join(', ')
    changes.push(`Removed ${count} attachment${count > 1 ? 's' : ''}: ${names}`)
  }

  if (changes.length === 0) {
    return 'No changes'
  }

  return changes.join('\n')
}

const getDiscrepancyFieldLabel = (
  field: string,
  t: (key: string) => string
): string => {
  if (field === 'amount') {
    return t('AMOUNT')
  }
  if (field === 'status') {
    return t('STATUS')
  }
  if (field === 'reason' || field === 'carerReason') {
    return t('REASON')
  }
  return capitalizeFieldName(field)
}

const formatDiscrepancyChanges = (
  oldValue: any,
  newValue: any,
  t: (key: string) => string
): React.ReactNode[] => {
  if (
    !oldValue ||
    !newValue ||
    typeof oldValue !== 'object' ||
    typeof newValue !== 'object'
  ) {
    return []
  }

  const changes: React.ReactNode[] = []

  if (oldValue.amount !== newValue.amount) {
    const fieldLabel = getDiscrepancyFieldLabel('amount', t)
    changes.push(
      <>
        Changed the {fieldLabel} from{' '}
        <BoldText as="span">{oldValue.amount ?? '-'}</BoldText> to{' '}
        <BoldText as="span">{newValue.amount ?? '-'}</BoldText>
      </>
    )
  }

  if (oldValue.status !== newValue.status) {
    const fieldLabel = getDiscrepancyFieldLabel('status', t)
    changes.push(
      <>
        Changed the {fieldLabel} from{' '}
        <BoldText as="span">{oldValue.status ?? '-'}</BoldText> to{' '}
        <BoldText as="span">{newValue.status ?? '-'}</BoldText>
      </>
    )
  }

  if (oldValue.reason !== newValue.reason) {
    const fieldLabel = getDiscrepancyFieldLabel('reason', t)
    changes.push(
      <>
        Changed the {fieldLabel} from{' '}
        <BoldText as="span">{oldValue.reason ?? '-'}</BoldText> to{' '}
        <BoldText as="span">{newValue.reason ?? '-'}</BoldText>
      </>
    )
  }

  if (oldValue.carerReason !== newValue.carerReason) {
    const fieldLabel = getDiscrepancyFieldLabel('carerReason', t)
    changes.push(
      <>
        Changed the {fieldLabel} from{' '}
        <BoldText as="span">{oldValue.carerReason ?? '-'}</BoldText> to{' '}
        <BoldText as="span">{newValue.carerReason ?? '-'}</BoldText>
      </>
    )
  }

  return changes
}

const formatObjectValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '-'
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => formatObjectValue(v)).join(', ')
    }

    if (value.amount !== undefined || value.status) {
      const parts: string[] = []
      if (value.amount !== undefined) parts.push(`Amount: ${value.amount}`)
      if (value.status) parts.push(`Status: ${value.status}`)
      if (value.reason) parts.push(`Reason: ${value.reason}`)
      if (value.carerReason) parts.push(`Reason: ${value.carerReason}`)
      return parts.join(', ')
    }

    return JSON.stringify(value)
  }

  return String(value)
}

const isUserIdField = (fieldName: string): boolean => {
  const lowerField = fieldName.toLowerCase()
  return (
    lowerField.includes('updatedby') ||
    lowerField.includes('resolvedby') ||
    lowerField.includes('recordedby') ||
    lowerField.includes('receivedby') ||
    lowerField.includes('actionedby') ||
    lowerField.includes('disposedby')
  )
}

const formatUserName = (value: any): string => {
  if (!value) return '-'

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'object' && value.name) {
    if (typeof value.name === 'string') {
      return value.name
    }

    if (
      typeof value.name === 'object' &&
      (value.name.first || value.name.last)
    ) {
      const parts: string[] = []
      if (value.name.first) parts.push(value.name.first)
      if (value.name.last) parts.push(value.name.last)
      return parts.join(' ') || '-'
    }
  }

  return '-'
}

export const formatFieldChange = (
  fieldName: string,
  oldValue: any,
  newValue: any,
  t: (key: string) => string,
  isDisposal?: boolean,
  isRestock?: boolean
): React.ReactNode => {
  const fieldLabel = getFieldLabel(fieldName, t, isDisposal, isRestock)

  if (isAttachmentField(fieldName)) {
    const text = formatAttachmentChange(oldValue, newValue)
    const parts: React.ReactNode[] = []
    const regex =
      /(Added \d+ new attachment[s]?: |Removed \d+ attachment[s]?: )(.+)/g
    let match = regex.exec(text)
    let lastIndex = 0

    while (match !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index))
      }
      parts.push(match[1])
      parts.push(
        <BoldText key={match.index} as="span">
          {match[2]}
        </BoldText>
      )
      lastIndex = regex.lastIndex
      match = regex.exec(text)
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts.length > 0 ? <>{parts}</> : text
  }

  if (fieldName === 'discrepancy') {
    if (typeof oldValue === 'string' && typeof newValue === 'string') {
      const statusLabel = getDiscrepancyFieldLabel('status', t)
      return (
        <>
          Changed the {statusLabel} from{' '}
          <BoldText as="span">{oldValue || '-'}</BoldText> to{' '}
          <BoldText as="span">{newValue || '-'}</BoldText>
        </>
      )
    }
    const changes = formatDiscrepancyChanges(oldValue, newValue, t)
    if (changes.length === 0) {
      return 'No changes'
    }
    return (
      <>
        {changes.map((change, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {change}
          </React.Fragment>
        ))}
      </>
    )
  }

  if (fieldName.startsWith('discrepancy.')) {
    const subField = fieldName.replace('discrepancy.', '')
    const subFieldLabel = getDiscrepancyFieldLabel(subField, t)

    return (
      <>
        Changed the {subFieldLabel} from{' '}
        <BoldText as="span">{oldValue ?? '-'}</BoldText> to{' '}
        <BoldText as="span">{newValue ?? '-'}</BoldText>
      </>
    )
  }

  if (isUserIdField(fieldName)) {
    const oldName = formatUserName(oldValue)
    const newName = formatUserName(newValue)

    if (oldName === newName && newName !== '-') {
      return <BoldText as="span">{newName}</BoldText>
    }

    return (
      <>
        Changed the {fieldLabel} from <BoldText as="span">{oldName}</BoldText>{' '}
        to <BoldText as="span">{newName}</BoldText>
      </>
    )
  }

  if (fieldName === 'actionTime') {
    const oldDate = oldValue ? formatDateOnly(oldValue) : '-'
    const newDate = newValue ? formatDateOnly(newValue) : '-'
    return (
      <>
        Changed the {fieldLabel} from <BoldText as="span">{oldDate}</BoldText>{' '}
        to <BoldText as="span">{newDate}</BoldText>
      </>
    )
  }

  if (fieldName === 'status') {
    const oldStatus = oldValue ? t(oldValue) : '-'
    const newStatus = newValue ? t(newValue) : '-'
    return (
      <>
        Changed the {fieldLabel} from <BoldText as="span">{oldStatus}</BoldText>{' '}
        to <BoldText as="span">{newStatus}</BoldText>
      </>
    )
  }

  const oldArray = Array.isArray(oldValue)
    ? oldValue
    : oldValue !== undefined && oldValue !== null
      ? [oldValue]
      : []
  const newArray = Array.isArray(newValue)
    ? newValue
    : newValue !== undefined && newValue !== null
      ? [newValue]
      : []

  const oldText = oldArray
    .map((v) => formatObjectValue(v))
    .filter((v) => v !== null && v !== undefined && v !== '')
    .join(', ')
  const newText = newArray
    .map((v) => formatObjectValue(v))
    .filter((v) => v !== null && v !== undefined && v !== '')
    .join(', ')

  return (
    <>
      Changed the {fieldLabel} from{' '}
      <BoldText as="span">{oldText || '-'}</BoldText> to{' '}
      <BoldText as="span">{newText || '-'}</BoldText>
    </>
  )
}
